'use client';

import { Milestone } from 'lucide-react';
import { FASES } from './AvanceProceso';
import { menuVisible } from '@/lib/menu';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

// Mapa de ruta del ciclo disciplinario completo (guía general, NO el avance de
// un expediente puntual — para eso está <AvanceProceso>, con el mismo orden y
// las mismas etiquetas para que se reconozca de un vistazo).
// A cada fase le corresponde, cuando aplica, la vista del Sidebar donde se
// trabaja esa parte del proceso.
const VISTA_POR_FASE: (string | null)[] = [
  'actas-ver',                // Radicación (las EN_PROCESO viven en Actas de reparto → Ver)
  'reparto-bandeja',          // Reparto
  'mis-expedientes',          // Instrucción
  null,                        // Pliego y remisión (parte de Instrucción, sin pantalla propia)
  'juzgamiento-bandeja',       // Juzgamiento
  'juzgamiento-fallos',        // Fallo y recursos
  'segunda-bandeja',           // Segunda instancia
  null,                        // Ejecución (se trabaja dentro de Segunda instancia)
  null,                        // Archivo (se consulta en el detalle del expediente)
];

// Qué fase(s) le corresponden a cada rol (para resaltar "tu parte" del mapa).
const FASES_POR_ROL: Record<string, number[]> = {
  ADMINISTRADOR: [0, 1, 2, 3, 4, 5, 6, 7, 8],
  TECNICO_ADMINISTRATIVO: [0],
  APOYO_TECNICO_ADMINISTRATIVO: [0],
  DIRECTOR_INSTRUCCION: [1, 2, 3],
  FUNCIONARIO_INSTRUCCION: [2, 3],
  CONTRATISTA_INSTRUCCION: [2, 3],
  TECNOLOGO_APOYO: [2, 3],
  ABOGADO_ESPECIALISTA: [2, 3],
  SUPERVISOR: [2],
  DIRECTOR_JUZGAMIENTO: [4, 5],
  FUNCIONARIO_JUZGAMIENTO: [4, 5],
  CONTRATISTA_JUZGAMIENTO: [4, 5],
  SEGUNDA_INSTANCIA: [6, 7],
  DIRECTOR_SEGUNDA_INSTANCIA: [6, 7],
};

export default function MapaProceso({
  rol,
  onNavegar,
}: {
  rol: string;
  onNavegar: (v: string) => void;
}) {
  const propias = new Set(FASES_POR_ROL[rol] ?? []);
  // Aplana el menú RECURSIVAMENTE (hay subgrupos anidados, p. ej. Actas de
  // reparto → Ver/Reportes dentro de la sección Instrucción).
  const vistasPermitidas = new Set(
    menuVisible(rol).flatMap(function ids(m): string[] {
      return m.submenu ? m.submenu.flatMap(ids) : [m.id];
    }),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Milestone className="size-4.5 text-primary" />
          Mapa del proceso disciplinario
        </CardTitle>
        <CardDescription>
          {propias.size
            ? 'Así fluye un expediente de principio a fin. Resaltado, lo que corresponde a tu rol.'
            : 'Así fluye un expediente de principio a fin, de la radicación al archivo.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ol className="flex items-start gap-0 overflow-x-auto pb-1">
          {FASES.map((fase, i) => {
            const esTuya = propias.has(i);
            const vista = VISTA_POR_FASE[i];
            const esClicable = !!vista && vistasPermitidas.has(vista);
            const Tag = esClicable ? 'button' : 'div';

            return (
              <li key={fase} className="flex min-w-[100px] flex-1 flex-col items-center">
                <div className="flex w-full items-center">
                  <span className={cn('h-0.5 flex-1', i === 0 ? 'opacity-0' : 'bg-border')} />
                  <span
                    className={cn(
                      'flex size-8 shrink-0 items-center justify-center rounded-full border-2 text-xs font-semibold',
                      esTuya
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-background text-muted-foreground',
                    )}
                  >
                    {i + 1}
                  </span>
                  <span
                    className={cn('h-0.5 flex-1', i === FASES.length - 1 ? 'opacity-0' : 'bg-border')}
                  />
                </div>

                <Tag
                  type={esClicable ? 'button' : undefined}
                  onClick={esClicable ? () => onNavegar(vista as string) : undefined}
                  className={cn(
                    'mt-1.5 flex flex-col items-center rounded-md px-1.5 py-1 text-center',
                    esClicable && 'cursor-pointer transition-colors hover:bg-primary/5',
                  )}
                >
                  <span
                    className={cn(
                      'text-[11px] leading-tight',
                      esTuya ? 'font-semibold text-foreground' : 'text-muted-foreground',
                    )}
                  >
                    {fase}
                  </span>
                  {esTuya && <span className="text-[10px] text-primary">Tu área</span>}
                </Tag>
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}
